// ═══════════════════════════════════════════════════════════════
// VaultPage — Page principale du coffre-fort utilisateur
// ═══════════════════════════════════════════════════════════════
// Sessions F (P1a) + F+1 (polish) :
//   • Liste cross-projet items chiffrés
//   • Lecture déchiffrée modale (no-cache)
//   • Suppression soft + restore 30j (onglet Corbeille)
//   • Export chiffré .json.enc (passphrase user, jamais stockée)
//   • Panneau Settings (burnThreshold, autoBurn)
//   • Historique d'accès (transparence)

import React, { useEffect, useState, useCallback } from 'react';
import {
    Lock, Trash2, Eye, X, RefreshCw, AlertTriangle, Download,
    Settings as SettingsIcon, History, Undo2, Clock, ShieldAlert,
    Copy, Check, KeyRound,
} from 'lucide-react';
import JSZip from 'jszip';
import {
    listVaultItems, readVaultItem, deleteVaultItem,
    restoreVaultItem, exportVault, getVaultSettings,
    updateVaultSettings, getVaultAccessLog, unlockVault,
    listVaultTrash,
    VaultApiError,
} from '../../services/vaultApi';
import { toast, confirm as confirmModal } from '../ui/Toast';
import { DECRYPT_VAULT_JS, DECRYPT_VAULT_PY, VAULT_README_MD } from './vaultExportAssets';
import { useVaultZk } from './VaultZkContext';
import MarkdownMessage from '../chat/MarkdownMessage';
import VaultZkSettingsPanel from './VaultZkSettingsPanel';
import VaultZkUnlockModal from './VaultZkUnlockModal';
import { decryptItem } from '../../utils/vaultZkCrypto';

const TABS = [
    { id: 'active',  label: 'Actifs',         icon: Lock },
    { id: 'trash',   label: 'Corbeille (30j)', icon: Trash2 },
    { id: 'log',     label: 'Historique',     icon: History },
    { id: 'settings', label: 'Paramètres',    icon: SettingsIcon },
];

const VaultPageInner = () => {
    const { dek, isUnlocked, challenge: zkChallenge, refreshChallenge } = useVaultZk();

    const [tab, setTab] = useState('active');
    const [items, setItems] = useState([]);
    const [trashItems, setTrashItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [readingId, setReadingId] = useState(null);
    const [readingContent, setReadingContent] = useState(null);
    const [readingMeta, setReadingMeta] = useState(null);

    const [settings, setSettings] = useState(null);
    const [accessLog, setAccessLog] = useState([]);

    const [exportOpen, setExportOpen] = useState(false);
    const [exportPass, setExportPass] = useState('');
    const [exporting, setExporting] = useState(false);

    const loadActive = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await listVaultItems();
            setItems(r.items || []);
        } catch (e) { setError(e.message || 'Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    const loadTrash = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await listVaultTrash();
            setTrashItems(r.items || []);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, []);

    const loadLog = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await getVaultAccessLog(50);
            setAccessLog(r.entries || []);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, []);

    const loadSettings = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const s = await getVaultSettings();
            setSettings(s);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (tab === 'active')   loadActive();
        if (tab === 'trash')    loadTrash();
        if (tab === 'log')      loadLog();
        if (tab === 'settings') loadSettings();
    }, [tab, loadActive, loadTrash, loadLog, loadSettings]);

    // Au mount : récupère l'état ZK pour savoir si on doit afficher la modale de déverrouillage
    useEffect(() => { refreshChallenge(); }, [refreshChallenge]);

    const zkActive = zkChallenge?.vault_zk_enabled === true;
    const showUnlockModal = zkActive && !isUnlocked;

    const handleRead = async (item) => {
        setReadingId(item.id);
        try {
            const data = await readVaultItem(item.id);
            // Item V2 : le serveur renvoie l'envelope, on déchiffre côté navigateur
            if (data.vaultZkEnabled === true && data.envelope) {
                if (!dek) {
                    toast.error('Coffre-fort verrouillé. Saisis ta passphrase.');
                    setReadingId(null);
                    return;
                }
                const envStr = `${data.envelope.iv}.${data.envelope.ciphertext_b64}`;
                const plaintext = await decryptItem(envStr, dek);
                if (plaintext == null) {
                    toast.error('Déchiffrement local échoué (clé incorrecte ou item corrompu).');
                    setReadingId(null);
                    return;
                }
                setReadingContent(plaintext);
                setReadingMeta(data);
                return;
            }
            setReadingContent(data.content);
            setReadingMeta(data);
        } catch (e) {
            if (e instanceof VaultApiError && e.status === 423) {
                toast.error('Coffre-fort verrouillé temporairement.');
            } else if (e instanceof VaultApiError && e.status === 410) {
                toast.error('Coffre-fort détruit (auto-destruction).');
            } else {
                toast.error('Lecture impossible : ' + (e.message || 'erreur'));
            }
            setReadingId(null);
        }
    };

    const handleClose = () => { setReadingId(null); setReadingContent(null); setReadingMeta(null); };

    const handleDelete = async (item) => {
        const ok = await confirmModal(
            `Supprimer cet item du coffre-fort ?`,
            { title: 'Suppression', danger: true, confirmLabel: 'Supprimer' }
        );
        if (!ok) return;
        try {
            await deleteVaultItem(item.id);
            toast.success('Item supprimé (récupérable 30 jours)');
            setItems(prev => prev.filter(i => i.id !== item.id));
        } catch (e) {
            toast.error('Erreur : ' + (e.message || 'inconnue'));
        }
    };

    const handleRestore = async (id) => {
        try {
            await restoreVaultItem(id);
            toast.success('Item restauré');
            // Rafraîchir vue courante (trash si on est dessus, active sinon)
            setTrashItems(prev => prev.filter(i => i.id !== id));
            if (tab === 'active') loadActive();
        } catch (e) {
            toast.error('Restauration impossible : ' + (e.message || ''));
        }
    };

    const handleExport = async () => {
        if (exportPass.length < 12) {
            toast.error('Passphrase trop courte (12 caractères minimum)');
            return;
        }
        setExporting(true);
        try {
            const env = await exportVault(exportPass);

            // Bundle dans un vrai ZIP : envelope chiffrée + 2 scripts de déchiffrement
            // (Node + Python, autonomes) + README. L'utilisateur dézippe et a tout
            // ce qu'il faut pour relire localement, sans que rien ne re-transite.
            const zip = new JSZip();
            zip.file('vault.json.enc',     JSON.stringify(env, null, 2));
            zip.file('decrypt-vault.js',   DECRYPT_VAULT_JS);
            zip.file('decrypt-vault.py',   DECRYPT_VAULT_PY);
            zip.file('README.md',          VAULT_README_MD);

            const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `itriia-vault-export-${stamp}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);

            toast.success(`Export téléchargé (${env.item_count} item·s chiffrés + scripts)`);
            setExportPass(''); setExportOpen(false);
        } catch (e) {
            toast.error('Export impossible : ' + (e.message || ''));
        } finally {
            setExporting(false);
        }
    };

    const handleUpdateSettings = async (patch) => {
        try {
            await updateVaultSettings(patch);
            toast.success('Paramètres mis à jour');
            loadSettings();
        } catch (e) {
            toast.error('Erreur : ' + (e.message || ''));
        }
    };

    const handleUnlock = async () => {
        try {
            await unlockVault();
            toast.success('Coffre déverrouillé');
            loadSettings();
        } catch (e) {
            if (e instanceof VaultApiError && e.status === 423) {
                toast.error('Coffre encore verrouillé.');
            } else if (e instanceof VaultApiError && e.status === 410) {
                toast.error('Coffre détruit (irréversible).');
            } else {
                toast.error('Erreur : ' + (e.message || ''));
            }
        }
    };

    const formatDate = (iso) => {
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };

    const formatBytes = (n) => {
        if (!n) return '—';
        if (n < 1024) return `${n} o`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
        return `${(n / 1024 / 1024).toFixed(2)} Mo`;
    };

    const purpleStyle = {
        bg: 'rgba(167,139,250,0.12)',
        border: 'rgba(167,139,250,0.3)',
        text: '#a78bfa',
    };

    return (
        <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '32px 40px 56px 40px', maxWidth: 1100, margin: '0 auto' }}>
            {showUnlockModal && zkChallenge && (
                <VaultZkUnlockModal challenge={zkChallenge} />
            )}
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                <div style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(192,132,252,0.10))',
                    border: '1px solid rgba(167,139,250,0.32)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Lock size={20} style={{ color: '#a78bfa' }} />
                </div>
                <div>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                        Coffre-fort utilisateur
                    </h1>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        Items chiffrés, isolés de la mémoire IA — visibles par vous seul.
                    </div>
                </div>
                <button
                    onClick={() => setExportOpen(true)}
                    style={{
                        marginLeft: 'auto',
                        background: purpleStyle.bg,
                        border: `1px solid ${purpleStyle.border}`, borderRadius: 6,
                        padding: '7px 12px', cursor: 'pointer',
                        color: purpleStyle.text, fontSize: 12, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 6,
                    }}
                >
                    <Download size={12} /> Exporter (chiffré)
                </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginTop: 18, borderBottom: '1px solid var(--card-border)' }}>
                {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setTab(id)}
                        style={{
                            background: tab === id ? purpleStyle.bg : 'transparent',
                            border: 'none',
                            borderBottom: tab === id ? `2px solid ${purpleStyle.text}` : '2px solid transparent',
                            color: tab === id ? purpleStyle.text : 'var(--text-secondary)',
                            padding: '8px 14px', cursor: 'pointer',
                            fontSize: 12.5, fontWeight: 500,
                            display: 'flex', alignItems: 'center', gap: 6,
                            transition: 'all 0.15s',
                        }}
                    >
                        <Icon size={13} /> {label}
                    </button>
                ))}
            </div>

            <div style={{ marginTop: 22 }}>

                {/* État chargement / erreur global */}
                {loading && (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                        Chargement…
                    </div>
                )}
                {error && (
                    <div style={{
                        padding: 16, borderRadius: 8,
                        background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)',
                        color: '#ef4444', display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16,
                    }}>
                        <AlertTriangle size={16} /> {error}
                    </div>
                )}

                {/* TAB ACTIFS */}
                {tab === 'active' && !loading && !error && (
                    <>
                        <div style={{
                            marginBottom: 16,
                            background: 'rgba(167,139,250,0.05)',
                            border: '1px solid rgba(167,139,250,0.18)',
                            borderRadius: 8, padding: '12px 14px',
                            fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)',
                        }}>
                            <strong style={{ color: '#a78bfa' }}>Promesse coffre-fort.</strong> Aucun outil
                            IA, aucun service de mémoire, aucun résumé n'a accès à ces items.
                            Le contenu reste chiffré au repos (AES-256-GCM).
                        </div>

                        {items.length === 0 ? (
                            <EmptyState
                                icon={Lock}
                                title="Votre coffre-fort est vide."
                                hint="Depuis un chat projet, cliquez sur l'icône cadenas d'un message pour l'envoyer ici."
                            />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {items.map(item => (
                                    <ItemRow
                                        key={item.id}
                                        item={item}
                                        onRead={() => handleRead(item)}
                                        onDelete={() => handleDelete(item)}
                                        formatDate={formatDate}
                                        formatBytes={formatBytes}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* TAB TRASH — items soft-deleted des 30 derniers jours */}
                {tab === 'trash' && !loading && !error && (
                    <>
                        <div style={{
                            marginBottom: 16,
                            background: 'rgba(245,158,11,0.06)',
                            border: '1px solid rgba(245,158,11,0.22)',
                            borderRadius: 8, padding: '12px 14px',
                            fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)',
                        }}>
                            <strong style={{ color: '#f59e0b' }}>Récupération 30 jours.</strong> Les
                            items supprimés depuis moins de 30 jours sont récupérables. Au-delà,
                            ils sont effacés définitivement par la rétention.
                        </div>

                        {trashItems.length === 0 ? (
                            <EmptyState
                                icon={Trash2}
                                title="Corbeille vide."
                                hint="Aucun item supprimé dans les 30 derniers jours."
                            />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {trashItems.map(item => (
                                    <TrashRow
                                        key={item.id}
                                        item={item}
                                        onRestore={() => handleRestore(item.id)}
                                        formatDate={formatDate}
                                        formatBytes={formatBytes}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* TAB LOG */}
                {tab === 'log' && !loading && !error && (
                    <AccessLogPanel entries={accessLog} formatDate={formatDate} />
                )}

                {/* TAB SETTINGS */}
                {tab === 'settings' && !loading && !error && settings && (
                    <>
                        <VaultZkSettingsPanel />
                        <SettingsPanel
                            settings={settings}
                            onUpdate={handleUpdateSettings}
                            onUnlock={handleUnlock}
                        />
                    </>
                )}
            </div>

            {/* Modale lecture */}
            {readingId && (
                <ReadModal
                    meta={readingMeta}
                    content={readingContent}
                    onClose={handleClose}
                />
            )}

            {/* Modale export */}
            {exportOpen && (
                <ExportModal
                    pass={exportPass}
                    setPass={setExportPass}
                    busy={exporting}
                    onClose={() => { if (!exporting) { setExportOpen(false); setExportPass(''); } }}
                    onConfirm={handleExport}
                />
            )}
        </div>
        </div>
    );
};

// VaultZkProvider est désormais hissé dans App.js — DEK partagé global
// (utilisé aussi par SendToVaultModal pour le chiffrement client-side V2).
const VaultPage = () => <VaultPageInner />;

// ─── Sous-composants ───────────────────────────────────────────

const EmptyState = ({ icon: Icon, title, hint }) => (
    <div style={{
        padding: '60px 20px', textAlign: 'center',
        color: 'var(--text-secondary)', fontSize: 13.5,
    }}>
        <Icon size={36} style={{ opacity: 0.35, marginBottom: 12 }} />
        <div style={{ marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 12, maxWidth: 480, margin: '0 auto' }}>{hint}</div>
    </div>
);

const ItemRow = ({ item, onRead, onDelete, formatDate, formatBytes }) => (
    <div style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto auto auto',
        gap: 14, alignItems: 'center',
        padding: '12px 14px',
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 8,
    }}>
        <Lock size={14} style={{ color: '#a78bfa', opacity: 0.7 }} />
        <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                {item.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                {item.role === 'user' ? 'Question utilisateur' : item.role === 'assistant' ? 'Réponse IA' : 'Item'} · projet #{item.projectId} · {formatBytes(item.sizeBytes)}
            </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{formatDate(item.createdAt)}</div>
        <button
            onClick={onRead}
            title="Lire le contenu (déchiffrement)"
            style={{
                background: 'rgba(167,139,250,0.12)',
                border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6,
                padding: '4px 10px', cursor: 'pointer',
                color: '#a78bfa', fontSize: 11.5,
                display: 'flex', alignItems: 'center', gap: 4,
            }}
        >
            <Eye size={11} /> Lire
        </button>
        <button
            onClick={onDelete}
            title="Supprimer (récupérable 30 jours)"
            style={{
                background: 'none', border: '1px solid var(--card-border)',
                borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
                color: '#ef4444', fontSize: 11.5,
                display: 'flex', alignItems: 'center',
            }}
        >
            <Trash2 size={11} />
        </button>
        <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
            padding: '2px 6px', borderRadius: 4,
            background: 'rgba(239,68,68,0.10)', color: '#ef4444',
            opacity: 0.7,
        }}>
            INVISIBLE IA
        </span>
    </div>
);

const TrashRow = ({ item, onRestore, formatDate, formatBytes }) => {
    const remaining = item.remainingDays ?? 0;
    const colorFor = remaining <= 3 ? '#ef4444' : remaining <= 7 ? '#f59e0b' : 'var(--text-secondary)';
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto auto auto',
            gap: 14, alignItems: 'center',
            padding: '12px 14px',
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 8, opacity: 0.85,
        }}>
            <Trash2 size={14} style={{ color: '#f59e0b', opacity: 0.7 }} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', textDecoration: 'line-through', textDecorationColor: 'rgba(239,68,68,0.4)' }}>
                    {item.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {item.role === 'user' ? 'Question utilisateur' : item.role === 'assistant' ? 'Réponse IA' : 'Item'} · projet #{item.projectId} · {formatBytes(item.sizeBytes)}
                </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Supprimé : {formatDate(item.deletedAt)}
            </div>
            <div style={{
                fontSize: 11, fontWeight: 700, color: colorFor,
                padding: '2px 8px', borderRadius: 4,
                background: `${colorFor}15`,
            }}>
                J-{remaining}
            </div>
            <button
                onClick={onRestore}
                title="Restaurer dans le coffre-fort"
                style={{
                    background: 'rgba(34,197,94,0.12)',
                    border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6,
                    padding: '4px 10px', cursor: 'pointer',
                    color: '#22c55e', fontSize: 11.5,
                    display: 'flex', alignItems: 'center', gap: 4,
                }}
            >
                <Undo2 size={11} /> Restaurer
            </button>
        </div>
    );
};

const ReadModal = ({ meta, content, onClose }) => {
    const [rawView, setRawView] = useState(false);
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* noop */ }
    };
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(4px)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: 12, width: 'min(820px, 94vw)', maxHeight: '88vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 28px 64px rgba(0,0,0,0.5)',
            }}>
                <div style={{
                    padding: '16px 22px', borderBottom: '1px solid var(--card-border)',
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <Lock size={16} style={{ color: '#a78bfa' }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                            {meta?.name || 'Item coffre-fort'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                            Déchiffré en mémoire navigateur uniquement · no-cache
                        </div>
                    </div>
                    <button
                        onClick={() => setRawView(v => !v)}
                        title={rawView ? 'Voir le rendu markdown' : 'Voir le markdown brut'}
                        style={{
                            background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)',
                            color: '#a78bfa', padding: '5px 10px', borderRadius: 6,
                            cursor: 'pointer', fontSize: 11.5, fontWeight: 500,
                        }}
                    >{rawView ? 'Rendu' : 'Brut'}</button>
                    <button
                        onClick={handleCopy}
                        title="Copier le contenu"
                        style={{
                            background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)',
                            color: '#a78bfa', padding: '5px 10px', borderRadius: 6,
                            cursor: 'pointer', fontSize: 11.5, fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                    >
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                        {copied ? 'Copié' : 'Copier'}
                    </button>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-secondary)',
                    }}>
                        <X size={18} />
                    </button>
                </div>
                <div style={{
                    padding: '20px 26px', overflow: 'auto', flex: 1,
                    color: 'var(--text)', lineHeight: 1.6,
                    ...(rawView
                        ? { fontFamily: 'var(--font-mono, monospace)', fontSize: 13, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }
                        : { fontSize: 14 }
                    ),
                }}>
                    {rawView
                        ? (content || '')
                        : <MarkdownMessage content={content || ''} />
                    }
                </div>
            </div>
        </div>
    );
};

// Génération passphrase forte via WebCrypto. 32 bytes hex = 64 chars =
// largement au-dessus du seuil minimum backend (12 chars).
const generateStrongPassphrase = () => {
    const buf = new Uint8Array(32);
    window.crypto.getRandomValues(buf);
    return Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
};

const ExportModal = ({ pass, setPass, busy, onClose, onConfirm }) => {
    const [autoMode, setAutoMode] = useState(true);   // true = passphrase auto-générée, false = saisie manuelle
    const [copied, setCopied] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    // Initialise une passphrase auto à l'ouverture (et à chaque toggle vers auto)
    useEffect(() => {
        if (autoMode) {
            setPass(generateStrongPassphrase());
        } else {
            setPass('');
        }
        setCopied(false);
        setAcknowledged(false);
    }, [autoMode, setPass]);

    const handleRegenerate = () => {
        setPass(generateStrongPassphrase());
        setCopied(false);
        setAcknowledged(false);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(pass);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch {
            // Fallback ancien navigateur : selectionner le champ
        }
    };

    const canDownload = !busy && pass.length >= 12 && (autoMode ? acknowledged : true);

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: 12, padding: '24px 26px', width: 'min(520px, 94vw)',
                color: 'var(--text)', boxShadow: '0 24px 56px rgba(0,0,0,0.4)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <Download size={16} style={{ color: '#a78bfa' }} />
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Export chiffré du coffre</div>
                </div>

                {autoMode ? (
                    <>
                        <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                            ITRIIA a généré une passphrase forte pour cette archive.
                            <strong style={{ color: 'var(--text)' }}> Copiez-la dans un endroit sûr</strong>
                            {' '}(password manager, papier). Elle ne sera <strong>jamais</strong> conservée
                            côté serveur — sans elle, l'archive est illisible.
                        </p>

                        <div style={{
                            background: 'rgba(167,139,250,0.06)',
                            border: '1px solid rgba(167,139,250,0.3)',
                            borderRadius: 8, padding: '12px 14px',
                            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                        }}>
                            <KeyRound size={14} style={{ color: '#a78bfa', flexShrink: 0 }} />
                            <code style={{
                                flex: 1, fontSize: 11.5, fontFamily: 'var(--font-mono, monospace)',
                                wordBreak: 'break-all', color: 'var(--text)', userSelect: 'all',
                            }}>{pass}</code>
                            <button
                                onClick={handleCopy}
                                title="Copier dans le presse-papier"
                                style={{
                                    background: copied ? 'rgba(34,197,94,0.18)' : 'rgba(167,139,250,0.15)',
                                    border: '1px solid ' + (copied ? 'rgba(34,197,94,0.4)' : 'rgba(167,139,250,0.35)'),
                                    color: copied ? '#22c55e' : '#a78bfa',
                                    padding: '5px 9px', borderRadius: 5,
                                    cursor: 'pointer', fontSize: 11, fontWeight: 600,
                                    display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                                }}
                            >
                                {copied ? <><Check size={11} /> Copié</> : <><Copy size={11} /> Copier</>}
                            </button>
                            <button
                                onClick={handleRegenerate}
                                title="Régénérer une nouvelle passphrase"
                                style={{
                                    background: 'transparent', border: '1px solid var(--card-border)',
                                    color: 'var(--text-secondary)',
                                    padding: '5px 7px', borderRadius: 5, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', flexShrink: 0,
                                }}
                            >
                                <RefreshCw size={11} />
                            </button>
                        </div>

                        <label style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                            fontSize: 12, color: 'var(--text-secondary)',
                            cursor: 'pointer', padding: '8px 0', userSelect: 'none',
                        }}>
                            <input
                                type="checkbox"
                                checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                style={{ marginTop: 2 }}
                            />
                            <span>
                                J'ai copié cette passphrase ailleurs en lieu sûr.
                                Je comprends qu'<strong style={{ color: '#f59e0b' }}>ITRIIA ne pourra pas
                                la récupérer si je la perds</strong>.
                            </span>
                        </label>
                    </>
                ) : (
                    <>
                        <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                            Saisissez votre propre passphrase (minimum 12 caractères, mélange recommandé
                            de mots, chiffres et symboles). <strong>Ne sera pas stockée par ITRIIA.</strong>
                        </p>
                        <input
                            type="password"
                            value={pass}
                            onChange={(e) => setPass(e.target.value)}
                            placeholder="Passphrase (12 caractères minimum)"
                            disabled={busy}
                            autoFocus
                            style={{
                                width: '100%', padding: '10px 12px', fontSize: 13,
                                background: 'var(--input-bg)', color: 'var(--text)',
                                border: '1px solid var(--card-border)', borderRadius: 6,
                                outline: 'none',
                            }}
                        />
                    </>
                )}

                <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-muted)' }}>
                    <button
                        type="button"
                        onClick={() => setAutoMode(!autoMode)}
                        disabled={busy}
                        style={{
                            background: 'none', border: 'none',
                            color: '#a78bfa', cursor: 'pointer',
                            padding: 0, fontSize: 11.5, textDecoration: 'underline',
                        }}
                    >
                        {autoMode ? 'Saisir ma propre passphrase' : 'Utiliser une passphrase générée par ITRIIA'}
                    </button>
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                    <button
                        onClick={onClose} disabled={busy}
                        style={{
                            background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                            color: 'var(--text-secondary)', padding: '8px 14px', borderRadius: 6,
                            cursor: busy ? 'not-allowed' : 'pointer', fontSize: 13,
                        }}
                    >Annuler</button>
                    <button
                        onClick={onConfirm}
                        disabled={!canDownload}
                        style={{
                            background: canDownload ? '#a78bfa' : 'rgba(167,139,250,0.4)',
                            border: 'none', color: 'white', padding: '8px 16px', borderRadius: 6,
                            cursor: canDownload ? 'pointer' : 'not-allowed',
                            fontSize: 13, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <Download size={12} />
                        {busy ? 'Chiffrement…' : 'Télécharger'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AccessLogPanel = ({ entries, formatDate }) => {
    if (!entries.length) {
        return <EmptyState icon={History} title="Aucune activité enregistrée." hint="Les actions sur le coffre-fort apparaîtront ici (création, lecture, suppression, restauration, échec)." />;
    }
    const actionStyle = (action) => {
        if (action === 'fail' || action === 'burn') return { color: '#ef4444', label: action === 'burn' ? 'AUTO-DESTRUCTION' : 'ÉCHEC' };
        if (action === 'create') return { color: '#22c55e', label: 'CRÉÉ' };
        if (action === 'read')   return { color: '#a78bfa', label: 'LU' };
        if (action === 'delete') return { color: '#f59e0b', label: 'SUPPRIMÉ' };
        if (action === 'restore') return { color: '#22c55e', label: 'RESTAURÉ' };
        if (action === 'export') return { color: '#3b82f6', label: 'EXPORTÉ' };
        if (action === 'unlock') return { color: '#22c55e', label: 'DÉVERROUILLÉ' };
        return { color: 'var(--text-secondary)', label: action.toUpperCase() };
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {entries.map(e => {
                const s = actionStyle(e.action);
                return (
                    <div key={e.id} style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto auto',
                        gap: 14, alignItems: 'center',
                        padding: '8px 12px',
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 6,
                    }}>
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                            padding: '2px 6px', borderRadius: 3,
                            background: `${s.color}1a`, color: s.color,
                        }}>{s.label}</span>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {e.fileId ? `Item #${e.fileId}` : '—'}
                            {e.projectId ? ` · projet #${e.projectId}` : ''}
                            {e.metadata?.reason ? ` · ${e.metadata.reason}` : ''}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{e.ip || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{formatDate(e.createdAt)}</div>
                    </div>
                );
            })}
        </div>
    );
};

const SettingsPanel = ({ settings, onUpdate, onUnlock }) => {
    const [threshold, setThreshold] = useState(settings.burnThreshold);
    const [autoBurn, setAutoBurn] = useState(settings.autoBurn);
    const isLocked = settings.lockedUntil && new Date(settings.lockedUntil) > new Date();
    const isBurned = !!settings.burnedAt;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {isBurned && (
                <div style={{
                    padding: 14, borderRadius: 8,
                    background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.3)',
                    color: '#ef4444', display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                    <ShieldAlert size={18} />
                    <div>
                        <strong>Coffre-fort détruit.</strong>{' '}
                        L'auto-destruction a été déclenchée le {new Date(settings.burnedAt).toLocaleString('fr-FR')}.
                        Cette action est irréversible (sauf restauration depuis backup chiffré).
                    </div>
                </div>
            )}

            {isLocked && !isBurned && (
                <div style={{
                    padding: 14, borderRadius: 8,
                    background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.3)',
                    color: '#f59e0b', display: 'flex', gap: 10, alignItems: 'center',
                }}>
                    <Clock size={16} />
                    <div style={{ flex: 1 }}>
                        Coffre verrouillé jusqu'au {new Date(settings.lockedUntil).toLocaleString('fr-FR')} suite à des tentatives répétées.
                    </div>
                    <button
                        onClick={onUnlock}
                        style={{
                            background: 'rgba(245,158,11,0.18)', border: '1px solid rgba(245,158,11,0.4)',
                            color: '#f59e0b', borderRadius: 6, padding: '6px 12px', fontSize: 12,
                            cursor: 'pointer', fontWeight: 600,
                        }}
                    >Déverrouiller</button>
                </div>
            )}

            <Section title="État">
                <Row label="Items actifs">{settings.itemCount}</Row>
                <Row label="Tentatives échouées">{settings.failedAttempts}</Row>
                <Row label="Verrouillé jusqu'au">{settings.lockedUntil ? new Date(settings.lockedUntil).toLocaleString('fr-FR') : '—'}</Row>
            </Section>

            <Section title="Sécurité">
                <Row label="Seuil de verrouillage">
                    <input
                        type="number" min="3" max="20"
                        value={threshold}
                        onChange={(e) => setThreshold(parseInt(e.target.value) || 3)}
                        style={{
                            width: 60, padding: '4px 8px', fontSize: 12,
                            background: 'var(--input-bg)', color: 'var(--text)',
                            border: '1px solid var(--card-border)', borderRadius: 4,
                        }}
                    />
                    <span style={{ marginLeft: 8, color: 'var(--text-secondary)', fontSize: 11 }}>tentatives</span>
                </Row>
                <Row label="Auto-destruction sur seuil">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={autoBurn} onChange={(e) => setAutoBurn(e.target.checked)} />
                        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                            Soft-supprime tous les items à seuil atteint (irréversible si vault_burned_at posé)
                        </span>
                    </label>
                </Row>
                <button
                    onClick={() => onUpdate({ burnThreshold: threshold, autoBurn })}
                    disabled={isBurned}
                    style={{
                        marginTop: 4, alignSelf: 'flex-start',
                        background: '#a78bfa', border: 'none', color: 'white',
                        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: isBurned ? 'not-allowed' : 'pointer', opacity: isBurned ? 0.5 : 1,
                    }}
                >Enregistrer</button>
            </Section>
        </div>
    );
};

const Section = ({ title, children }) => (
    <div style={{
        padding: '14px 18px', background: 'var(--card-bg)',
        border: '1px solid var(--card-border)', borderRadius: 8,
    }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
);

const Row = ({ label, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
        <span style={{ color: 'var(--text-secondary)', minWidth: 200 }}>{label}</span>
        <span style={{ color: 'var(--text)' }}>{children}</span>
    </div>
);

export default VaultPage;
